import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// PrisonLock — an industrial padlock built in SVG that gates entry to the
// deck. The user types the key on the locket's recessed plate; on success
// the hasp lifts, the body swings open and falls away, then `onUnlock()`
// fires. On failure the locket shakes and pulses red.
//
// The expected key is stored as a SHA-256 hex digest so the plaintext never
// appears in the bundle. (It's still client-side — this is brand theatre, not
// real authentication.)
//
// CURRENT KEY = "ANL@2026"
// SHA-256("ANL@2026") = 2c95457bcbc0d9fa84f81d7cbf33416073d05bdb978d28991ceff9c5854f7aa3

const EXPECTED_HASH =
  '2c95457bcbc0d9fa84f81d7cbf33416073d05bdb978d28991ceff9c5854f7aa3';

async function sha256(text) {
  const enc = new TextEncoder().encode(text);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export default function PrisonLock({ onUnlock }) {
  const [value, setValue] = useState('');
  const [state, setState] = useState('idle'); // idle | wrong | unlocked
  const inputRef = useRef(null);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (state === 'unlocked') return;
    const hash = await sha256(value.trim().toUpperCase());
    if (hash === EXPECTED_HASH) {
      setState('unlocked');
      // Wait for the unlock animation to complete, then notify parent
      setTimeout(() => onUnlock && onUnlock(), 1400);
    } else {
      setState('wrong');
      // Briefly flash red, then return to idle so the user can retype
      setTimeout(() => setState('idle'), 700);
      try { inputRef.current?.select(); } catch (_) {}
    }
  };

  // Animation variants for the lock body + hasp
  const bodyVariants = {
    idle: { y: 0, rotate: 0, opacity: 1 },
    wrong: { x: [0, -10, 10, -7, 7, -3, 0], transition: { duration: 0.45 } },
    unlocked: {
      y: 80, rotate: -6, opacity: 0,
      transition: { duration: 1.2, ease: [0.6, 0, 0.4, 1] },
    },
  };
  const haspVariants = {
    idle: { y: 0, rotate: 0 },
    wrong: { y: 0, rotate: 0 },
    unlocked: {
      y: -22, rotate: 18,
      transition: { duration: 0.55, ease: 'easeOut' },
    },
  };

  return (
    <div
      data-testid="prison-lock"
      className="relative flex flex-col items-center select-none"
      style={{ width: 'min(360px, 80vw)' }}
    >
      {/* Caption above the lock */}
      <div className="font-ui text-[10px] tracking-[0.45em] uppercase text-[color:var(--gold)]/80 mb-3">
        SEALED · ENTER THE KEY
      </div>

      {/* The locket itself */}
      <motion.div
        className="relative"
        variants={bodyVariants}
        animate={state}
        initial="idle"
        style={{
          filter: state === 'wrong'
            ? 'drop-shadow(0 0 16px rgba(220,40,40,0.6))'
            : 'drop-shadow(0 22px 30px rgba(0,0,0,0.65)) drop-shadow(0 0 14px rgba(200,162,74,0.18))',
        }}
      >
        <svg
          width="100%"
          viewBox="0 0 360 460"
          xmlns="http://www.w3.org/2000/svg"
          style={{ display: 'block' }}
        >
          <defs>
            {/* Brushed gunmetal gradient */}
            <linearGradient id="bodyGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor="#2a2522" />
              <stop offset="0.4" stopColor="#3a322c" />
              <stop offset="0.7" stopColor="#221d1a" />
              <stop offset="1" stopColor="#15110f" />
            </linearGradient>
            {/* Subtle inner panel inset gradient */}
            <linearGradient id="panelGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor="#0e0a09" />
              <stop offset="1" stopColor="#1a1411" />
            </linearGradient>
            {/* Gold accent gradient for the engraving */}
            <linearGradient id="goldGrad" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0" stopColor="#8b6a2a" />
              <stop offset="0.5" stopColor="#f2d27a" />
              <stop offset="1" stopColor="#8b6a2a" />
            </linearGradient>
            {/* Hasp brushed steel */}
            <linearGradient id="haspGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor="#544b46" />
              <stop offset="0.5" stopColor="#807368" />
              <stop offset="1" stopColor="#34302b" />
            </linearGradient>
            {/* Weathered scratch noise */}
            <filter id="scratch" x="0" y="0" width="1" height="1" filterUnits="objectBoundingBox" primitiveUnits="objectBoundingBox">
              <feTurbulence type="fractalNoise" baseFrequency="2.5" numOctaves="2" seed="7" />
              <feColorMatrix values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.18 0" />
              <feComposite in2="SourceGraphic" operator="in" />
            </filter>
          </defs>

          {/* HASP — the U-shaped bar at top */}
          <motion.g
            variants={haspVariants}
            animate={state}
            initial="idle"
            style={{ transformOrigin: '110px 180px' }}
          >
            <path
              d="M 110 180 L 110 110 A 70 70 0 0 1 250 110 L 250 180"
              fill="none"
              stroke="url(#haspGrad)"
              strokeWidth="26"
              strokeLinecap="round"
            />
            <path
              d="M 110 180 L 110 110 A 70 70 0 0 1 250 110 L 250 180"
              fill="none"
              stroke="rgba(255,255,255,0.18)"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </motion.g>

          {/* LOCK BODY */}
          <g>
            {/* Outer body with rounded corners */}
            <rect x="40" y="165" width="280" height="270" rx="34" ry="34" fill="url(#bodyGrad)" stroke="#0c0908" strokeWidth="2" />
            {/* Top highlight strip */}
            <rect x="48" y="172" width="264" height="3" rx="2" fill="rgba(255,225,160,0.18)" />
            {/* Weathered scratch overlay */}
            <rect x="40" y="165" width="280" height="270" rx="34" ry="34" fill="rgba(255,255,255,0.6)" filter="url(#scratch)" opacity="0.5" />

            {/* Engraved "BLACK AXE" label */}
            <text
              x="180" y="218"
              textAnchor="middle"
              style={{ fontFamily: 'var(--font-deco, "Playfair Display", serif)', fontSize: 24, letterSpacing: 4 }}
              fill="url(#goldGrad)"
              opacity="0.95"
            >
              BLACK AXE
            </text>
            <text
              x="180" y="236"
              textAnchor="middle"
              style={{ fontFamily: 'var(--font-ui, "Source Code Pro", monospace)', fontSize: 8, letterSpacing: 6 }}
              fill="rgba(200,162,74,0.7)"
            >
              · NO. AKR-668 ·
            </text>

            {/* Recessed inner panel where the password input lives */}
            <rect x="62" y="252" width="236" height="62" rx="10" fill="url(#panelGrad)" stroke="#000" strokeWidth="2" />
            <rect x="62" y="252" width="236" height="62" rx="10" fill="none" stroke="rgba(200,162,74,0.45)" strokeWidth="0.8" />

            {/* Keyhole */}
            <g transform="translate(180, 358)">
              <circle r="14" fill="#0a0706" stroke="rgba(200,162,74,0.55)" strokeWidth="1.5" />
              <circle r="6" fill="#1a1411" />
              <rect x="-2.5" y="0" width="5" height="18" fill="#0a0706" />
            </g>

            {/* 4 corner rivets */}
            {[
              [62, 187], [298, 187], [62, 413], [298, 413],
            ].map(([cx, cy], i) => (
              <g key={i} transform={`translate(${cx},${cy})`}>
                <circle r="7" fill="#0a0706" />
                <circle r="5" fill="url(#goldGrad)" />
                <circle r="2" fill="rgba(255,255,255,0.25)" cx="-1.4" cy="-1.4" />
              </g>
            ))}

            {/* Wear marks bottom edge */}
            <path d="M 80 432 Q 180 442 280 432" stroke="rgba(0,0,0,0.45)" strokeWidth="1.5" fill="none" />
          </g>
        </svg>

        {/* HTML password input layered over the recessed panel */}
        <form
          onSubmit={submit}
          className="absolute"
          style={{
            // Match the SVG inner panel coordinates: x=62 y=252 width=236 height=62
            // SVG is 360x460, so these are percentages of the rendered locket.
            top: `${(252 / 460) * 100}%`,
            left: `${(62 / 360) * 100}%`,
            width: `${(236 / 360) * 100}%`,
            height: `${(62 / 460) * 100}%`,
          }}
        >
          <input
            ref={inputRef}
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="ENTER THE KEY"
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            inputMode="text"
            enterKeyHint="go"
            data-testid="prison-lock-input"
            className="w-full h-full bg-transparent outline-none text-center font-ui tracking-[0.5em] uppercase text-[color:var(--gold)] caret-[color:var(--gold)] placeholder:text-[color:var(--gold)]/40"
            style={{ fontSize: 'clamp(13px, 1.6vw, 18px)' }}
          />
          <button
            type="submit"
            data-testid="prison-lock-submit"
            aria-label="Unlock"
            className="sr-only"
          >
            unlock
          </button>
        </form>
      </motion.div>

      {/* Visible UNLOCK button — required for mobile browsers (iOS Safari /
          Android Chrome) where the keyboard's Go/Return key often does NOT
          fire form submit on password inputs with a hidden submit button.
          Also acts as a clear tap target for any user who doesn't realize
          the Enter key is the trigger. */}
      <button
        type="button"
        onClick={submit}
        data-testid="prison-lock-unlock-button"
        aria-label="Unlock the deck"
        className="mt-5 group relative inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full border border-[color:var(--gold)]/60 bg-black/65 backdrop-blur-sm hover:border-[color:var(--gold)] active:scale-[0.97] transition-[transform,border-color,background-color] duration-150 touch-manipulation"
        style={{ minHeight: '48px', minWidth: '180px' }}
      >
        <span className="font-ui text-[11px] tracking-[0.42em] uppercase text-[color:var(--gold)]">
          UNLOCK
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-[color:var(--gold)] transition-transform duration-200 group-hover:translate-x-0.5">
          <path d="M5 12h14M13 5l7 7-7 7" />
        </svg>
      </button>

      {/* Feedback line under the lock */}
      <div className="h-6 mt-3">
        <AnimatePresence mode="wait">
          {state === 'wrong' && (
            <motion.div
              key="wrong"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="font-ui text-[10px] tracking-[0.35em] uppercase blood-text"
              data-testid="prison-lock-wrong"
            >
              KEY REJECTED · TRY AGAIN
            </motion.div>
          )}
          {state === 'unlocked' && (
            <motion.div
              key="ok"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-ui text-[10px] tracking-[0.35em] uppercase text-[color:var(--gold)]"
              data-testid="prison-lock-ok"
            >
              SEAL BROKEN · ENTERING THE DECK
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Hint */}
      <div className="mt-2 font-ui text-[9px] tracking-[0.4em] uppercase text-[color:var(--text-dim)]/70 text-center">
        TAP UNLOCK · OR PRESS ENTER
      </div>
    </div>
  );
}
